import {
  LuClock as Clock,
  LuCalendar as Calendar,
  LuGlobe as Globe,
  LuClipboardEdit as Edit,
  LuTrash2 as Trash2,
} from "react-icons/lu";
import { ScheduledPost } from "../../types/scheduled-posts.entity";
import { Attachments } from "../posts/attachments";
import { useState } from "react";
import { EditScheduledPost } from "./edit-scheduled-post";
import { Link } from "react-router-dom";
import { DeleteScheduledPost } from "./delete-scheduled-post";

type ScheduledPostCardProps = {
  post: ScheduledPost;
};

export const ScheduledPostCard = ({ post }: ScheduledPostCardProps) => {
  const [deleteMode, setDeleteMode] = useState<boolean>(false);

  // Format the scheduled date
  const scheduledDate = post.scheduledDate.toDate();
  const formattedDate = scheduledDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const formattedTime = scheduledDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  // Check if post is scheduled for future
  const isUpcoming = scheduledDate > new Date();

  return (
    <>
      <div className="group relative overflow-hidden border-0 shadow-sm hover:shadow-lg transition-all duration-300 bg-white/50 backdrop-blur-sm rounded-lg border bg-white text-gray-900">
        {/* Subtle gradient border effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="flex flex-col space-y-2 p-4 border-b relative pb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 space-y-3">
              {/* Admin Badge and Status */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold text-sm">
                    A
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Admin Post</p>
                    <div className="flex items-center gap-1">
                      {post.langCodes?.map((code) => (
                        <div key={code} className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Globe className="w-3 h-3" />
                          <span className="uppercase tracking-wide">{code}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${isUpcoming ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"
                    }`}
                >
                  {isUpcoming ? "Scheduled" : "Past"}
                </div>
              </div>

              {/* Scheduled Date/Time */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>{formattedDate}</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span>{formattedTime}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-1">
              <Link
                to={"/feed/scheduled-posts/" + post.id + "/edit"}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                aria-label="Edit post"
              >
                <Edit className="w-4 h-4 text-gray-600" />
              </Link>
              <button
                onClick={() => setDeleteMode(true)}
                className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200"
                aria-label="Delete post"
              >
                <Trash2 className="w-4 h-4 text-red-600" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 relative space-y-5">
          {/* Post Content */}
          <div className="prose prose-sm max-w-none">
            <p className="text-gray-700 leading-relaxed">{post.content}</p>
          </div>

          {/* Attachments */}
          {post.attachments && post.attachments.length > 0 && <Attachments attachments={post.attachments} />}
        </div>
      </div>
      {deleteMode && <DeleteScheduledPost onClose={() => setDeleteMode(false)} post={post} />}
    </>
  );
};
